import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import { askQuestionStream } from "../../services/insightsApi";
import style from "./Insights.module.css";

const SUGGESTED_QUESTIONS = [
  "What are the most common complaints?",
  "How has sentiment changed over time?",
  "What do customers like most?",
  "Which categories have the most negative feedback?",
  "Summarize the key themes in recent feedback",
];

const InsightsAsk = () => {
  const org = useSelector((state) => state.orgStatus.value);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const industry = "Betting";
  const [geography, setGeography] = useState(
    () => localStorage.getItem("insights_geography") || "Nigeria"
  );
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleSend(question) {
    const q = question || input.trim();
    if (!q || !org?.payload || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: q }]);
    setLoading(true);

    const assistantIdx = messages.length + 1;
    setMessages((prev) => [
      ...prev,
      { role: "assistant", text: "", streaming: true },
    ]);

    try {
      await askQuestionStream(
        { orgId: org.payload, question: q, industry, geography },
        (chunk) => {
          setMessages((prev) => {
            const updated = [...prev];
            const msg = updated[assistantIdx];
            if (msg) updated[assistantIdx] = { ...msg, text: msg.text + chunk };
            return updated;
          });
        },
        (meta) => {
          if (meta.dataSize) {
            setMessages((prev) => {
              const updated = [...prev];
              const msg = updated[assistantIdx];
              if (msg) updated[assistantIdx] = { ...msg, dataSize: meta.dataSize };
              return updated;
            });
          }
        }
      );
      setMessages((prev) => {
        const updated = [...prev];
        const msg = updated[assistantIdx];
        if (msg) updated[assistantIdx] = { ...msg, streaming: false };
        return updated;
      });
    } catch (err) {
      setMessages((prev) => {
        const updated = [...prev];
        if (updated[assistantIdx]?.text) {
          updated[assistantIdx] = { ...updated[assistantIdx], streaming: false };
        } else {
          updated[assistantIdx] = { role: "error", text: err.message };
        }
        return updated;
      });
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="mainContentInner">
      <TitleBar
        title="Ask About Your Data"
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Insights", url: "/insights" },
        ]}
      />

      <div className={`contentPadding ${style.askContainer}`}>
        <div className={style.contextBar}>
          <div className={style.contextField}>
            <label>Geography</label>
            <select
              value={geography}
              onChange={(e) => {
                setGeography(e.target.value);
                localStorage.setItem("insights_geography", e.target.value);
              }}
            >
              <option value="Nigeria">Nigeria</option>
              <option value="Zambia">Zambia</option>
              <option value="South Africa">South Africa</option>
            </select>
          </div>
        </div>

        {messages.length === 0 ? (
          <div className={style.chatWelcome}>
            <Icon>forum</Icon>
            <h2>Ask questions about your feedback data</h2>
            <div className={style.chatInputBar}>
              <textarea
                className={style.chatInput}
                placeholder="Ask a question about your feedback data..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={loading}
              />
              <button
                className={style.sendButton}
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
              >
                <Icon>send</Icon>
              </button>
            </div>
            <div className={style.suggestedQuestions}>
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  className={style.suggestedQuestion}
                  onClick={() => handleSend(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className={style.chatArea}>
              {messages.map((msg, i) => (
                msg.streaming && !msg.text ? null :
                <div
                  key={i}
                  className={`${style.chatMessage} ${style[msg.role]}`}
                >
                  {msg.role === "user" && (
                    <div className={style.messageIcon}><Icon>person</Icon></div>
                  )}
                  {msg.role === "assistant" && (
                    <div className={style.messageIcon}><Icon>smart_toy</Icon></div>
                  )}
                  {msg.role === "error" && (
                    <div className={style.messageIcon}><Icon>error</Icon></div>
                  )}
                  <div className={style.messageContent}>
                    <div className={style.messageText}>
                      {msg.role === "assistant" ? (
                        <ReactMarkdown>{msg.text}</ReactMarkdown>
                      ) : (
                        msg.text
                      )}
                    </div>
                    {msg.dataSize && (
                      <div className={style.messageMeta}>
                        Based on {msg.dataSize} feedback records
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {loading && messages[messages.length - 1]?.text === "" && (
                <div className={`${style.chatMessage} ${style.assistant}`}>
                  <div className={style.messageIcon}><Icon>smart_toy</Icon></div>
                  <div className={style.messageContent}>
                    <div className={style.typingIndicator}>
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className={style.chatInputBar}>
              <textarea
                className={style.chatInput}
                placeholder="Ask a question about your feedback data..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={loading}
              />
              <button
                className={style.sendButton}
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
              >
                <Icon>send</Icon>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InsightsAsk;
